// frontend/src/components/asset/SecurePhotoThumb.js
/**
 * Authenticated photo thumbnail for JWT-protected asset photo endpoints.
 *
 * Bare <img src> / <a href> omit Authorization — the browser never attaches
 * the JWT. This fetches via the axios client (interceptors add the token)
 * and uses an object URL for both display and click-to-open.
 *
 * Mirrors SecureFileLink / AccidentPhotoThumb / InspectionPhoto.
 */

import React, { useState, useEffect } from 'react';
import { AlertCircle, ExternalLink } from 'lucide-react';
import { api } from '../../services/api';

const SecurePhotoThumb = ({
  photoUrl,
  alt = 'Photo',
  theme = 'admin',
  className = '',
  children = null,
}) => {
  const [url, setUrl] = useState(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let blobUrl;
    let cancelled = false;

    if (!photoUrl) {
      setFailed(true);
      setUrl(null);
      return undefined;
    }

    setFailed(false);
    setUrl(null);

    api.get(photoUrl, { responseType: 'blob' })
      .then((res) => {
        if (cancelled) return;
        blobUrl = URL.createObjectURL(res.data);
        setUrl(blobUrl);
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });

    return () => {
      cancelled = true;
      if (blobUrl) URL.revokeObjectURL(blobUrl);
    };
  }, [photoUrl]);

  const isEmployee = theme === 'employee';
  const ringHover = isEmployee ? 'hover:ring-blue-700' : 'hover:ring-red-500';
  const ringFocus = isEmployee ? 'focus-visible:ring-blue-500' : 'focus-visible:ring-red-500';
  const spinnerBorder = isEmployee ? 'border-blue-600' : 'border-red-500';

  if (failed) {
    return (
      <div
        className={`relative aspect-square bg-gray-100 rounded-lg overflow-hidden flex flex-col items-center justify-center p-4 ${className}`}
      >
        <AlertCircle className="w-8 h-8 text-gray-400 mb-2" />
        <p className="text-xs text-gray-500 text-center">Photo unavailable</p>
      </div>
    );
  }

  if (!url) {
    return (
      <div
        className={`relative aspect-square bg-gray-100 rounded-lg overflow-hidden flex items-center justify-center ${className}`}
      >
        <div className={`animate-spin rounded-full h-8 w-8 border-b-2 ${spinnerBorder}`} />
      </div>
    );
  }

  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      className={`group relative aspect-square bg-gray-200 rounded-lg overflow-hidden hover:ring-2 transition-all focus-visible:outline-none focus-visible:ring-2 ${ringFocus} focus-visible:ring-offset-1 ${ringHover} ${className}`}
    >
      <img src={url} alt={alt} className="w-full h-full object-cover" />
      {children}
      {/* Always visible on touch; stronger on hover — matches TransferDetailModal */}
      <div className="absolute inset-0 bg-black/20 group-hover:bg-black/40 transition-all flex items-center justify-center pointer-events-none">
        <ExternalLink className="w-5 h-5 text-white/70 group-hover:text-white group-hover:w-6 group-hover:h-6 transition-all" />
      </div>
    </a>
  );
};

export default SecurePhotoThumb;
